<template>
  <div>
    <content-placeholder v-if="fetching">
      <el-card>
        <p class="text text-75"></p>
        <p class="text text-full"></p>
        <p class="text text-50"></p>
        <p class="text text-50"></p>
        <p class="text text-50"></p>
      </el-card>
    </content-placeholder>

    <el-card v-else>
      <div slot="header">{{title}}</div>
      <el-table 
        style="width: 100%" 
        class="depth"
        :data="days">
        <el-table-column label="Date" align="right" width="70">
          <template slot-scope="scope">
            <b>{{ dateShort(scope.row.date) }}</b>
          </template>
        </el-table-column>
        <el-table-column label="Weekday" align="center">
          <template slot-scope="scope">
            {{ weekdayShort(scope.row.date) }}
          </template>
        </el-table-column>
        <el-table-column label="Start Time" align="right">
          <template slot-scope="scope">
            <div v-if="scope.row.start_time">
              {{ scope.row.start_time | formatTimeNoTZ('hh:mm a') }}
            </div>
            <div v-else>
              OFF
            </div>
          </template>
        </el-table-column>
        <el-table-column label="End Time" align="right">
          <template slot-scope="scope">
            <div v-if="scope.row.start_time">
              {{ scope.row.start_time | getEndTime(scope.row.work_length) | formatTimeNoTZ('hh:mm a') }}
            </div>
            <div v-else>
              OFF
            </div>
          </template>
        </el-table-column>
        <el-table-column label="Length" align="center">
          <template slot-scope="scope">
            <div v-if="scope.row.start_time">
              {{ scope.row.work_length | formatDiff('hours') }}
            </div>
            <div v-else>
              OFF
            </div>
          </template>
        </el-table-column>
        <el-table-column label="Break" align="center">
          <template slot-scope="scope">
            <div v-if="scope.row.start_time">
              {{ scope.row.break_length | formatDiff('minutes') }}
            </div>
            <div v-else>
              OFF
            </div>
          </template>
        </el-table-column>
        <el-table-column label="" width="120" align="right" v-if="currentUser.admin">
          <template slot-scope="scope">
            <el-button @click="removeSchedule(scope.row)" size="mini" type="danger" v-if="scope.row.start_time"><i class="fas fa-calendar-times"></i></el-button>
            <el-button @click="editSchedule(scope.row)" size="mini" type="success"><i class="far fa-calendar-edit"></i></el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <schedule-form-modal />
  </div>
</template>

<script>
  import moment from 'moment';
  import { merge, keyBy, values } from 'lodash';
  import { mapActions, mapGetters } from 'vuex';

  import { showSuccessMessage, showErrorMessage } from '@/ui/message';
  import durationMixin from '@/store/modules/timeLog/mixins/duration';
  import ScheduleFormModal from '@/components/schedule/ScheduleFormModal';

  export default {
    name: 'Schedule',
    props: {
      userId: {
        required: true,
        type: Number,
      },
      title: {
        type: String,
        default: 'Schedule',
      },
      week: {
        type: [Number, String],
        default: moment().week(),
      },
      year: {
        type: [Number, String],
        default: moment().year(),
      },
    },
    data() {
      return {
        today: moment(),
      };
    },
    mixins: [durationMixin],
    components: {
      scheduleFormModal: ScheduleFormModal,
    },
    created() {
      this.fetchSchedule({ userId: this.userId });
    },
    computed: {
      ...mapGetters('entities/schedules', ['fetching', 'isUpdating', 'isDeleting']),
      ...mapGetters('entities/users', ['userFromContext', 'currentUser']),

      schedules() {
        return this.$store.getters['entities/schedules/query']()
          .where('user_id', this.userId)
          .get();
      },
      startOfWeek() {
        return moment(`${this.week} ${this.year}`, 'ww gggg').startOf('week');
      },
      days() {
        const daysCount = 7;
        const days = [];
        const scheduled = JSON.parse(JSON.stringify(this.schedules));
        for (let i = 1; i < daysCount + 1; i += 1) {
          const date = this.startOfWeek.clone().add(i - 1, 'days');
          const obj = {
            day: i,
            date,
          };
          days.push(obj);
        }
        let merged = merge(keyBy(days, 'day'), keyBy(scheduled, 'day'));
        merged = values(merged);
        return merged;
      },
    },
    methods: {
      ...mapActions('entities/schedules', ['fetchSchedule', 'setContext', 'openFormModal', 'deleteSchedule']),

      dateShort(day) {
        return day.format('MMM DD');
      },
      dateLong(day) {
        return day.format('MMMM Do YYYY');
      },
      weekdayShort(day) {
        return day.format('ddd');
      },
      additionalModalContext(day) {
        if (this.userFromContext) {
          return {
            day,
            user_id: this.userFromContext.id,
          };
        }
        return {};
      },
      editSchedule(schedule) {
        if (schedule) {
          this.setContext(schedule.id);
          this.openFormModal(schedule.day);
        }
      },
      removeSchedule(schedule) {
        this.$confirm('Do you really want to delete this item?', 'Warning', {
          confirmButtonText: 'OK',
          cancelButtonText: 'Cancel',
          title: 'Confirm',
        }).then(() => {
          this.deleteSchedule(schedule.id)
            .then((response) => {
              if (response.status) {
                showSuccessMessage('Item has been deleted successfully');
              } else {
                showErrorMessage(response.error);
              }
            });
        });
      },
      rowClassStatus(scope) {
        const classes = [];
        const d = new Date();

        if (!scope.row.start_time) {
          classes.push('not-active-row');
        }
        if (moment(scope.row.date).format('ll') === moment(d).format('ll')) {
          classes.push('current-day');
        }
        return classes.join(' ');
      },
    },
  };
</script>

<style lang="scss">
  @import '../../assets/scss/variables';

  .not-active-row{
    color: #ccc;
  }
  .current-day{
    td:first-child{
      border-left: solid 4px $--saffron-red-delight;
    }
  }
  .el-table__expanded-cell{
    background-color: #e8e8e8;
    &:hover{
      background-color: #e8e8e8 !important;
    }
  }
</style>


// WEBPACK FOOTER //
// src/components/schedule/Schedule.vue